import { Fragment, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import StraightenIcon from '@mui/icons-material/Straighten';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, ButtonGroup, Divider, IconButton, Tooltip } from '@mui/material';

import { useTopicStore, useTopicViewerStore, useTopicWSStore } from '@/dmdrive/topic/components/Topic/TopicStore';
import { ConferenceState } from '@/dmdrive/topic/constants/topic';

import { ClipPlaneMode } from '@carbon/lod3d-viewer/lib/lod3d-viewer';

import { ViewerControlsRoot } from './ViewerControls.styles';

function ViewerControls() {
  const {
    isControlDisabled,
    topic,
    me,
    conferenceState,
    isJoinedConference,
    joinConference,
    leaveConference,
    setConferenceState,
  } = useTopicStore();
  const { requestForMaster, cancelRequestForMaster } = useTopicWSStore();
  const { shouldShowRemarks, toggleShowRemarks, remarksType, setRemarksType, viewerSettings, viewer } =
    useTopicViewerStore();

  const { clipPlaneMode, apply } = viewerSettings;

  const onToggleClipPlane = useCallback(
    (mode: ClipPlaneMode) => {
      apply({ clipPlaneMode: clipPlaneMode === mode ? 'off' : mode });
    },
    [clipPlaneMode],
  );

  const isCreatingConference = conferenceState === ConferenceState.Creating;
  const isFollow = conferenceState === ConferenceState.Follow;
  const isExplore = conferenceState === ConferenceState.Explore;
  const isLod3dControlsEnabled = me?.isMaster || isExplore || !isJoinedConference;

  return (
    <ViewerControlsRoot>
      {topic?.areRemarksAvailable && (
        <Fragment>
          <Tooltip title={`${shouldShowRemarks ? 'Hide' : 'Show'} Remarks`} placement="top">
            <IconButton
              disabled={isControlDisabled || (topic?.marks && topic.marks.length === 0)}
              onClick={toggleShowRemarks}
            >
              {shouldShowRemarks ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          <Tooltip title="Difference measurement" placement="top">
            <IconButton
              color={remarksType === 'DifferenceMeasurement' ? 'primary' : undefined}
              onClick={() => setRemarksType(remarksType === 'DifferenceMeasurement' ? 'none' : 'DifferenceMeasurement')}
            >
              <SquareFootIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Length measurement" placement="top">
            <IconButton
              color={remarksType === 'LengthMeasurement' ? 'primary' : undefined}
              onClick={() => setRemarksType(remarksType === 'LengthMeasurement' ? 'none' : 'LengthMeasurement')}
            >
              <StraightenIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Text remark" placement="top">
            <IconButton
              color={remarksType === 'Annotation' ? 'primary' : undefined}
              onClick={() => setRemarksType(remarksType === 'Annotation' ? 'none' : 'Annotation')}
            >
              <ChatBubbleIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />
        </Fragment>
      )}

      {topic?.isLODViewer && (
        <Fragment>
          <Tooltip title="Move model" placement="top">
            <IconButton
              disabled={isControlDisabled || !isLod3dControlsEnabled}
              color={clipPlaneMode === 'translate' ? 'primary' : undefined}
              onClick={() => onToggleClipPlane('translate')}
            >
              <ControlCameraIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Rotate model" placement="top">
            <IconButton
              disabled={isControlDisabled || !isLod3dControlsEnabled}
              color={clipPlaneMode === 'rotate' ? 'primary' : undefined}
              onClick={() => onToggleClipPlane('rotate')}
            >
              <Rotate90DegreesCcwIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />
        </Fragment>
      )}

      <Tooltip title="Fit camera to scene" placement="top">
        <IconButton onClick={() => viewer?.fitCameraToScene()} disabled={isControlDisabled}>
          <FullscreenExitIcon />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {!isJoinedConference && (
        <LoadingButton loading={isCreatingConference} onClick={joinConference} variant="contained">
          Join conference
        </LoadingButton>
      )}

      {isJoinedConference && (
        <Fragment>
          <ButtonGroup size="small">
            <Button
              disabled={isCreatingConference}
              variant={isFollow ? 'contained' : 'outlined'}
              onClick={() => setConferenceState(ConferenceState.Follow)}
            >
              Follow
            </Button>

            <Button
              disabled={isCreatingConference}
              variant={isExplore ? 'contained' : 'outlined'}
              onClick={() => setConferenceState(ConferenceState.Explore)}
            >
              Explore
            </Button>
          </ButtonGroup>

          {isJoinedConference && !me?.isMaster && (
            <Tooltip title={me?.requestMaster ? 'Cancel request master' : 'Request master'} placement="top">
              <IconButton
                color={me?.requestMaster ? 'primary' : undefined}
                onClick={me?.requestMaster ? cancelRequestForMaster : requestForMaster}
              >
                <WavingHandIcon />
              </IconButton>
            </Tooltip>
          )}

          <LoadingButton loading={isCreatingConference} onClick={leaveConference} variant="contained" color="error">
            Leave
          </LoadingButton>
        </Fragment>
      )}
    </ViewerControlsRoot>
  );
}

export default observer(ViewerControls);
